import { Check, ChevronDown, Copy, ExternalLink, MapPin } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import type { EventPlace } from "@/lib/calendarEventPlace";
import {
  copyCalendarLocationText,
  openCalendarLocationInMaps,
  type MapsProvider,
} from "@/lib/calendarLocationMaps";
import { isHubIosShell } from "@/lib/hubShell";

type Props = {
  place: Pick<EventPlace, "location" | "locationHref">;
};

export function CalendarEventLocation({ place }: Props) {
  const { location, locationHref } = place;
  const [copied, setCopied] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!menuOpen) return;
    function onDoc(event: MouseEvent) {
      const root = rootRef.current;
      if (!root) return;
      if (event.target instanceof Node && root.contains(event.target)) return;
      setMenuOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setMenuOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  if (!location) return null;

  async function onCopy(event: React.MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    const ok = await copyCalendarLocationText(location);
    if (!ok) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  }

  function openMaps(provider: MapsProvider) {
    openCalendarLocationInMaps(provider, location, locationHref);
    setMenuOpen(false);
  }

  function onPrimaryMaps(event: React.MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    openMaps(isHubIosShell() ? "apple" : "google");
  }

  return (
    <div className="cal-location" ref={rootRef}>
      <div className="cal-location-main">
        <MapPin size={14} aria-hidden className="cal-location-pin" />
        <button
          type="button"
          className="cal-location-text"
          onClick={() => setMenuOpen((open) => !open)}
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          aria-controls={menuId}
        >
          {locationHref ? (
            <span className="cal-location-text-inner" title={locationHref}>
              {location}
            </span>
          ) : (
            location
          )}
        </button>
      </div>
      <div className="cal-location-actions">
        <button
          type="button"
          className="cal-location-action"
          title={copied ? "Copied" : "Copy location"}
          aria-label={copied ? "Location copied" : "Copy location"}
          onClick={(event) => void onCopy(event)}
        >
          {copied ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />}
          <span>{copied ? "Copied" : "Copy"}</span>
        </button>
        <div className="cal-location-maps-wrap">
          <button
            type="button"
            className="cal-location-action cal-location-maps-btn"
            title="Open in Maps"
            aria-label="Open in Maps"
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            aria-controls={menuId}
            onClick={onPrimaryMaps}
          >
            <ExternalLink size={14} aria-hidden />
            <span>Maps</span>
          </button>
          <button
            type="button"
            className="icon-btn cal-location-maps-chevron"
            title="Choose maps app"
            aria-label="Choose maps app"
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            aria-controls={menuId}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setMenuOpen((open) => !open);
            }}
          >
            <ChevronDown size={14} aria-hidden />
          </button>
          {menuOpen ? (
            <div className="cal-location-menu" id={menuId} role="menu">
              <button type="button" role="menuitem" onClick={() => openMaps("apple")}>
                Apple Maps
              </button>
              <button type="button" role="menuitem" onClick={() => openMaps("google")}>
                Google Maps
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
